import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Search, MapPin, Users, Loader2 } from 'lucide-react';
import { mockStates } from '../data/mock';
import { facilityApi } from '../utils/api';

const StatesListPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [siteStats, setSiteStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await facilityApi.getStats();
        setSiteStats(response);
      } catch (err) {
        console.error('Error fetching site stats:', err);
      }
    };

    fetchStats();
  }, []);

  // mockStates supplies the real code/name/population; the per-state facility
  // count comes from the live database instead of that file's placeholder
  // numbers (see facilities_by_state on the stats endpoint).
  const states = mockStates.map((state) => ({
    ...state,
    facilities: siteStats?.facilities_by_state?.[state.code]?.count ?? null,
  }));

  const filteredStates = states.filter(state =>
    state.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalFacilities = siteStats?.total_facilities ?? null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-blue-600 to-purple-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Browse Treatment Centers by State
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Find addiction treatment facilities in all 50 states.{' '}
              {totalFacilities !== null
                ? `Over ${totalFacilities.toLocaleString()} treatment centers available nationwide.`
                : 'Loading treatment center counts...'}
            </p>
            
            {/* Search */}
            <div className="max-w-md mx-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <Input
                  type="text"
                  placeholder="Search states..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12 text-lg bg-white text-gray-900"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* States Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                All States ({filteredStates.length})
              </h2>
              <p className="text-gray-600">
                Click on any state to view treatment facilities and local resources.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredStates.map((state) => (
                <Link 
                  key={state.code}
                  to={`/states/${state.code.toLowerCase()}`}
                  className="block transition-transform hover:scale-105"
                >
                  <Card className="h-full hover:shadow-lg transition-shadow border-l-4 border-l-blue-500">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {state.name}
                        </h3>
                        <Badge variant="secondary" className="text-xs">
                          {state.code}
                        </Badge>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center text-blue-600">
                          <MapPin className="h-4 w-4 mr-2" />
                          <span className="font-medium">
                            {state.facilities !== null ? (
                              `${state.facilities} facilities`
                            ) : (
                              <Loader2 className="h-3 w-3 animate-spin inline" />
                            )}
                          </span>
                        </div>
                        
                        <div className="flex items-center text-gray-600">
                          <Users className="h-4 w-4 mr-2" />
                          <span>Pop: {state.population}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {filteredStates.length === 0 && (
              <div className="text-center py-12">
                <Search className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No states found
                </h3>
                <p className="text-gray-600">
                  Try adjusting your search terms.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Quick Access - Popular States */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-8">
              Most Popular States for Treatment
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {siteStats ? (
                [...states]
                  .sort((a, b) => b.facilities - a.facilities)
                  .slice(0, 8)
                  .map((state) => (
                    <Link
                      key={state.code}
                      to={`/states/${state.code.toLowerCase()}`}
                      className="block"
                    >
                      <Card className="text-center hover:shadow-lg transition-shadow">
                        <CardContent className="p-6">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {state.name}
                          </h3>
                          <div className="text-2xl font-bold text-blue-600 mb-1">
                            {state.facilities}
                          </div>
                          <div className="text-sm text-gray-600">
                            Treatment Centers
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))
              ) : (
                <div className="col-span-full flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Find Addiction Treatment Centers in Your State
            </h2>
            
            <div className="text-gray-600 space-y-4">
              <p>
                Addiction affects millions of Americans across all 50 states. Whether you're 
                looking for treatment for yourself or a loved one, finding the right facility 
                in your state is crucial for successful recovery.
              </p>
              
              <p>
                Our comprehensive directory includes treatment centers offering various levels 
                of care including:
              </p>
              
              <ul className="list-disc pl-6 space-y-1">
                <li>Inpatient residential treatment programs</li>
                <li>Outpatient therapy and counseling</li>
                <li>Medical detoxification services</li>
                <li>Intensive outpatient programs (IOP)</li>
                <li>Partial hospitalization programs (PHP)</li>
                <li>Sober living and transitional housing</li>
              </ul>
              
              <p>
                Each state page provides detailed information about local treatment options, 
                state-specific resources, crisis helplines, and statistics about addiction 
                in your area. We also include information about insurance coverage and 
                payment options available in each state.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StatesListPage;